import { useRef, useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Button,
} from "@mui/material";

import { ScoringPill } from "components/LeadershipOverview/components";
import { HideColumnsMenu } from "components/HideColumnsMenu";
import CustomIcon from "components/CustomIcon";
import { ICReportRestrictedView } from "pages/LeadershipDashboard/components/ICReportRestrictedView";
import { headerConfig, THeaderConfigProps } from "./table-config";

import { getAbilityIcon } from "utils";
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { camelCase, isEqual, startCase } from "lodash";

import { ICReportPerson } from "store/slice/Project/icDashboard/icDashboard.types";

import icons from "enums/icons";

const Management = ({
    data,
    tableHeader,
    setTableHeader,
}: {
    data: ICReportPerson[] | null;
    tableHeader: THeaderConfigProps[];
    setTableHeader: React.Dispatch<React.SetStateAction<THeaderConfigProps[]>>;
}) => {
    const theme = useTheme();
    const tableRef = useRef<HTMLDivElement>(null);

    const ability = useAbility(AbilityContext);
    const canSeeBoard = ability.can("see", "LD_PROJECT_VIEW");

    const initialHeaderConfig = useRef(headerConfig);
    const hidableColumns = tableHeader.filter((cell) => cell.hasOwnProperty("isHidden"));
    const visibleColumns = hidableColumns.filter((cell) => !cell.isHidden);
    const hasHeaderChangedSelection = !isEqual(tableHeader, initialHeaderConfig.current);

    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isEnd, setIsEnd] = useState(false);

    const handleToggleHeaderColumn = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, itemId: string | number) => {
        setTableHeader((prevVal) =>
            prevVal.map((cell) => {
                if (cell.hasOwnProperty("isHidden") && cell.id === itemId) {
                    return { ...cell, isHidden: !cell.isHidden };
                }
                return cell;
            })
        );
    };

    const handleResetColumns = () => {
        setTableHeader(initialHeaderConfig.current);
    };

    const capitalizeName = (name: string) => {
        return startCase(camelCase(name));
    };

    const handleScroll = () => {
        const node = tableRef.current;
        if (!node) return;

        if (isEnd) {
            // Back to start
            node.scrollTo({ left: 0, behavior: "smooth" });
        } else {
            // Go to end
            node.scrollTo({ left: node.scrollWidth, behavior: "smooth" });
        }
    };

    // Overflow and scroll position
    useEffect(() => {
        const node = tableRef.current;

        if (!node) return;

        const checkScrollPosition = () => {
            const { scrollWidth, clientWidth, scrollLeft } = node;
            const isOverflow = scrollWidth > clientWidth;
            const isEnd = scrollLeft + clientWidth >= scrollWidth;
            setShowScrollButton(isOverflow);
            setIsEnd(isEnd);
        };

        checkScrollPosition();
        node.addEventListener("scroll", checkScrollPosition);
        window.addEventListener("resize", checkScrollPosition);

        return () => {
            node.removeEventListener("scroll", checkScrollPosition);
            window.removeEventListener("resize", checkScrollPosition);
        };
    }, []);

    return (
        <>
            <ScoringPill isBoardPage />

            {showScrollButton && (
                <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button variant="text" size="small" onClick={handleScroll}>
                        <Box display="flex" alignItems="center">
                            {isEnd && <CustomIcon icon={icons.arrowLeft} sx={{ fontSize: "16px", mr: 1 }} />}
                            <Typography sx={{ color: theme.palette.text.primary }}>Scroll To See More</Typography>
                            {!isEnd && <CustomIcon icon={icons.arrowRight} sx={{ fontSize: "16px", ml: 1 }} />}
                        </Box>
                    </Button>
                </Box>
            )}

            {canSeeBoard ? (
                <TableContainer ref={tableRef} sx={{ overflowX: "hidden" }}>
                    <Table sx={{ my: 1, p: 1 }}>
                        <TableHead sx={{ background: theme.palette.background.grey, mb: 1 }}>
                            <TableRow>
                                {tableHeader
                                    .filter((item) => !item.isHidden)
                                    .map((item) => (
                                        <TableCell
                                            key={item.id}
                                            sx={{
                                                ...item.sx,
                                                ...(item.id === "name-role" && {
                                                    position: "sticky",
                                                    left: 0,
                                                    background: theme.palette.background.grey,
                                                    zIndex: 2,
                                                }),
                                            }}
                                        >
                                            {item.name}
                                        </TableCell>
                                    ))}
                                <TableCell sx={{ width: 26 }} align="center">
                                    <HideColumnsMenu
                                        isICReport
                                        items={hidableColumns}
                                        onClickItem={handleToggleHeaderColumn}
                                        onResetColumns={handleResetColumns}
                                        disableReset={!hasHeaderChangedSelection}
                                        icon={<CustomIcon icon={icons.threeDots} sx={{ fontSize: 24 }} />}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.map((item) => (
                                <TableRow key={item.member_id}>
                                    <TableCell
                                        sx={{
                                            py: 0.5,
                                            position: "sticky",
                                            left: 0,
                                            background: theme.palette.background.default,
                                            zIndex: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1">
                                                {capitalizeName(item.member_name)}
                                            </Typography>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ color: theme.palette.text.secondary }}
                                            >
                                                {item.job_title ?? ""}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    {visibleColumns
                                        .filter((col) => col.id !== "name-role")
                                        .map((col: THeaderConfigProps) => {
                                            const iconColor = item.ability_scores[col.id]?.RAG?.colour ?? null;

                                            return (
                                                <TableCell key={col.id} align="center" sx={{ py: 0.5 }}>
                                                    <CustomIcon
                                                        icon={getAbilityIcon(iconColor)}
                                                        sx={{ fontSize: 36 }}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <>
                    <TableContainer>
                        <Table sx={{ my: 1, p: 1 }}>
                            <TableHead sx={{ background: theme.palette.background.grey, mb: 1 }}>
                                <TableRow>
                                    {tableHeader
                                        .filter((item) => !item.isHidden)
                                        .map((item) => (
                                            <TableCell
                                                key={item.id}
                                                padding={item.id === "name-role" ? "normal" : "none"}
                                                sx={item.sx}
                                            >
                                                {item.name}
                                            </TableCell>
                                        ))}
                                    <TableCell sx={{ width: 26 }} align="center">
                                        <HideColumnsMenu
                                            isICReport
                                            items={hidableColumns}
                                            onClickItem={handleToggleHeaderColumn}
                                            onResetColumns={handleResetColumns}
                                            disableReset={!hasHeaderChangedSelection}
                                            icon={<CustomIcon icon={icons.threeDots} sx={{ fontSize: 24 }} />}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Box>
                                            <Typography variant="subtitle1">
                                                {capitalizeName(data?.[0].member_name ?? "")}
                                            </Typography>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ color: theme.palette.text.secondary }}
                                            >
                                                {data?.[0].job_title}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    {visibleColumns
                                        .filter((col) => col.id !== "name-role")
                                        .map((col: THeaderConfigProps) => {
                                            const iconColor = data?.[0].ability_scores[col.id]?.RAG?.colour ?? null;

                                            return (
                                                <TableCell key={col.id} align="center" sx={(col.sx, { py: 0.5 })}>
                                                    <CustomIcon
                                                        icon={getAbilityIcon(iconColor)}
                                                        sx={{ fontSize: 36 }}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <ICReportRestrictedView isBoard />
                </>
            )}
        </>
    );
};

export default Management;
